import { jsxs, jsx } from 'react/jsx-runtime';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { IconChevronDownLarge } from '../../../../foundation/icons/IconChevronDownLarge/index.js';
import { IconLoading } from '../../../../foundation/icons/IconLoading/index.js';
import { badgeData } from '../data/index.js';
import { useSkinStyles, useLayoutStyles } from '../styles/index.js';
import { InlineButtonVariants, InlineButtonElementTypes, InlineButtonAlignments } from '../types/index.js';
import { ControlSizesXL } from '../../../helpers/controlSizes/controlSizes/index.js';
import { Badge } from '../../Badge/Badge/index.js';
import { computeResponsiveness } from '../../../../foundation/responsive/helpers/index.js';

const InlineButton = forwardRef(({ tag: Tag = InlineButtonElementTypes.Button, variant = InlineButtonVariants.Default, size = ControlSizesXL, alignment = InlineButtonAlignments.Center, badge, className, children, startIcon: StartIcon, endIcon: EndIcon, centerIcon: CenterIcon, isWithoutBorder, isWithoutBackground, isFullWidth, isActive, isDisabled, isLoading, ...props }, ref) => {
    const skinStyles = useSkinStyles({
        Tag,
        isWithoutBorder,
        isWithoutBackground,
        isActive,
        variant,
    });
    const sizes = computeResponsiveness(size);
    const layoutStyles = useLayoutStyles({
        Tag,
        centerIcon: CenterIcon,
        sizes,
        isWithoutBorder,
        isWithoutBackground,
        isFullWidth,
        isDisabled,
        isLoading,
        alignment,
    });
    return (jsxs(Tag, { ...(Tag !== InlineButtonElementTypes.Upload && { ...props }), ...(Tag === InlineButtonElementTypes.Button && { type: 'button' }), ref: ref, className: clsx(layoutStyles.root, {
            [skinStyles.default]: !isActive,
            [skinStyles.interactive]: !isActive && !isDisabled && !isLoading,
            [skinStyles.active]: isActive,
            [skinStyles.activeInteraction]: isActive && !isDisabled,
            [skinStyles.disabled]: isDisabled,
            [layoutStyles.loading]: isLoading,
        }, className), ...(Tag === InlineButtonElementTypes.Button && { disabled: isDisabled }), "data-testid": props['data-testid'] || 'inline-button', children: [Tag === InlineButtonElementTypes.Upload
                && jsx("input", { type: "file", className: layoutStyles.control, disabled: isDisabled, ...props, "data-testid": "button-input" }), StartIcon && !CenterIcon && !isLoading && jsx(StartIcon, { className: clsx(layoutStyles.icon), "data-testid": "start-icon" }), !isLoading && CenterIcon && jsx(CenterIcon, { className: layoutStyles.icon, "data-testid": "center-icon" }), children && !CenterIcon && (jsx("div", { className: layoutStyles.contentWrap, children: jsx("span", { className: layoutStyles.content, "data-testid": "content", children: children }) })), badge && !CenterIcon && (jsx(Badge, { className: layoutStyles.badge, size: sizes?.Default.value?.badgeSize, variant: badgeData[badge] })), (EndIcon && !CenterIcon && !isLoading && variant === InlineButtonVariants.Default) && jsx(EndIcon, { className: clsx(layoutStyles.icon), "data-testid": "end-icon" }), (!EndIcon && !CenterIcon && !isLoading && variant === InlineButtonVariants.Select) && jsx(IconChevronDownLarge, { className: clsx(layoutStyles.icon, 'arrow'), "data-testid": "arrow" }), isLoading && jsx(IconLoading, { className: clsx(layoutStyles.icon, layoutStyles.loadingIcon, skinStyles.loadingIcon), "data-testid": "loading-icon" })] }));
});
InlineButton.displayName = 'Button';

export { InlineButton };
